import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FileText, MessageSquare, MapPin, FileSignature, Wrench, GraduationCap, Rocket, HeartHandshake, IndianRupee, Building } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

gsap.registerPlugin(ScrollTrigger);

const FranchiseModel = () => {
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  const iconMap = {
    1: FileText,
    2: MessageSquare,
    3: MapPin,
    4: FileSignature,
    5: Wrench,
    6: GraduationCap,
    7: Rocket,
    8: HeartHandshake
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.step-card',
        { opacity: 0, scale: 0.8, y: 30 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'back.out(1.7)',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [language]);

  return (
    <section id="franchise" ref={sectionRef} className="py-20 lg:py-32 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-4 py-2 rounded-full text-sm font-semibold mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.franchise.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.franchise.title} <span className="text-red-600 dark:text-red-500">{t.franchise.highlight}</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.franchise.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-6 text-white">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
              <IndianRupee className="w-6 h-6" />
            </div>
            <p className="text-white/80 text-sm mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>{t.franchise.investment}</p>
            <p className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>{language === 'en' ? '₹12–15 Lakhs' : '₹१२–१५ लाख'}</p>
          </div>

          <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl p-6 text-white">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
              <Building className="w-6 h-6" />
            </div>
            <p className="text-white/80 text-sm mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>{t.franchise.model}</p>
            <p className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>FOFO</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {t.franchise.steps.map((step, index) => {
            const IconComponent = iconMap[index + 1];
            return (
              <div key={index} className="step-card relative">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 text-center hover:bg-red-50 dark:hover:bg-gray-700 transition-colors duration-300 border border-gray-100 dark:border-gray-700 hover:border-red-200 h-full">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-bold shadow-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {index + 1}
                    </div>
                  </div>

                  <div className="w-16 h-16 mx-auto rounded-2xl bg-white dark:bg-gray-700 shadow-md flex items-center justify-center mb-4 mt-2">
                    {IconComponent && <IconComponent className="w-8 h-8 text-red-500 dark:text-red-400" />}
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t.franchise.ctaLabel}
          </p>
          <a
            href="#enquiry"
            className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <Rocket className="w-5 h-5" />
            {t.franchise.ctaBtn}
          </a>
        </div>
      </div>
    </section>
  );
};

export default FranchiseModel;
